import React from 'react';
import { Modal, Tag, Typography, Spin, Card, Divider, message, Alert } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useUserPermissionModalStore } from '@/stores/userPermissionModal';
import { getPermissionList } from '@/api/permission';
import type { Permission } from '@/DTO/permission';
import type { RolePermission } from '@/DTO/role';

const { Text } = Typography;

const roleColors = {
  admin: 'red',
  manager: 'blue',
  auditor: 'green',
  user: 'default',
};

const GlobalUserPermissionModal: React.FC = () => {
  const { isOpen, user, loading, error, closeUserPermission } = useUserPermissionModalStore();

  // 使用 React Query 获取所有权限列表
  const { data: allPermissions = [], isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      const response = await getPermissionList({
        offset: 0,
        limit: 1000,
        order: 'desc',
      });

      if (response.code === 0) {
        return response.data.data;
      } else {
        message.error(response.message || '获取权限列表失败');
        return [];
      }
    },
    enabled: isOpen, // 只有弹窗打开时才执行查询
  });

  // 获取用户所有权限的详细信息
  const getUserPermissions = (): RolePermission[] => {
    if (!user || !user.roles) return [];

    const permissions: RolePermission[] = [];
    const addedPermissionIds = new Set<number>();

    user.roles.forEach((role) => {
      if (role.permissions) {
        role.permissions.forEach((permission) => {
          // 避免重复添加相同权限
          if (!addedPermissionIds.has(permission.id)) {
            permissions.push(permission);
            addedPermissionIds.add(permission.id);
          }
        });
      }
    });

    return permissions;
  };

  // 按模块分组所有权限（从所有权限列表中分组）
  const getGroupedPermissions = () => {
    return allPermissions.reduce(
      (groups, permission) => {
        // 从 flag 中提取模块信息（如 organization:list -> organization）
        const [module] = permission.flag.split(':');
        const moduleName = module || '其他';

        if (!groups[moduleName]) {
          groups[moduleName] = [];
        }
        groups[moduleName].push(permission);
        return groups;
      },
      {} as Record<string, Permission[]>,
    );
  };

  // 获取用户拥有的权限ID集合
  const getUserPermissionIds = (): Set<number> => {
    if (!user || !user.roles) return new Set();

    const permissionIds = new Set<number>();
    user.roles.forEach((role) => {
      if (role.permissions) {
        role.permissions.forEach((permission) => {
          permissionIds.add(permission.id);
        });
      }
    });

    return permissionIds;
  };

  const userPermissions = getUserPermissions();
  const userPermissionIds = getUserPermissionIds();
  const groupedPermissions = getGroupedPermissions();

  return (
    <Modal title="用户权限" open={isOpen} onCancel={closeUserPermission} footer={null} width={900} destroyOnClose>
      <Spin spinning={loading}>
        {error && <Alert message="获取用户信息失败" description={error} type="error" style={{ marginBottom: 16 }} showIcon />}

        {user && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>用户：</Text>
              <Text>{user.username}</Text>
              <br />
              <Text strong>角色：</Text>
              {user.roles?.map((role) => (
                <Tag key={role.id} color={roleColors[role.role_name as keyof typeof roleColors] || 'default'}>
                  {role.role_name}
                </Tag>
              ))}
              <br />
              <Text strong>权限数量：</Text>
              <Text>{userPermissions.length}</Text>
            </div>

            <Spin spinning={permissionsLoading}>
              <div>
                {Object.entries(groupedPermissions).map(([module, modulePermissions]) => {
                  const userHasPermissionsCount = modulePermissions.filter((p) => userPermissionIds.has(p.id)).length;

                  return (
                    <Card key={module} size="small" style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text strong style={{ color: '#1890ff' }}>
                          {module}
                        </Text>
                        <Tag color="blue">
                          {userHasPermissionsCount}/{modulePermissions.length} 个权限
                        </Tag>
                      </div>
                      <Divider style={{ margin: '12px 0' }} />
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                        {modulePermissions.map((permission) => {
                          const hasPermission = userPermissionIds.has(permission.id);

                          return (
                            <div
                              key={permission.id}
                              style={{
                                padding: '12px',
                                border: '1px solid #d9d9d9',
                                borderRadius: '6px',
                                backgroundColor: hasPermission ? '#f6ffed' : '#fafafa',
                                borderColor: hasPermission ? '#b7eb8f' : '#d9d9d9',
                                opacity: hasPermission ? 1 : 0.6,
                                minWidth: '120px',
                                textAlign: 'center',
                                cursor: 'default',
                              }}
                            >
                              <div style={{ fontSize: '14px', fontWeight: hasPermission ? 'bold' : 'normal', marginBottom: '4px' }}>
                                <Text style={{ color: hasPermission ? '#52c41a' : '#8c8c8c' }}>{permission.permission_name}</Text>
                              </div>
                              <div style={{ fontSize: '12px' }}>
                                <Text type="secondary" style={{ color: hasPermission ? '#73d13d' : '#bfbfbf' }}>
                                  {permission.flag}
                                </Text>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </Spin>

            <div style={{ marginTop: 16, fontSize: '12px', color: '#666' }}>
              <Text type="secondary">绿色高亮表示用户拥有的权限，灰色表示该模块中用户暂未拥有的权限。</Text>
            </div>
          </div>
        )}

        {!loading && !error && !user && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">暂无用户数据</Text>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default GlobalUserPermissionModal;
